import { useState } from 'react';
import {
  useReviewRequestsQuery,
  useDeleteReviewSendMutation,
  useSendReviewRequestsMutation,
} from '../../api/queries';
import { copyToClipboard, downloadImage } from '../../lib/clipboard';
import { showToast } from '../../components/Toast';
import { formatNumber } from '../../lib/format';
import type { ReviewRequestChannel, ReviewSendResult } from '../../types';

/**
 * "Ask for Reviews" — sits at the top of the Google Business Profile section.
 *
 * The franchisee gets their review link, a QR code and copy they can paste
 * into their own email or text, then records the send here. The dashboard
 * never takes a contact list: client contact data is not allowed on this box,
 * which is also why the NPS feature distributes links rather than emails.
 */

const CHANNEL_LABEL: Record<ReviewRequestChannel, string> = {
  email: 'Email',
  sms: 'Text message',
  print: 'Printed / QR',
  in_person: 'In person',
  other: 'Other',
};

type Props = { af: string };

export default function GbpReviewRequestsPanel({ af }: Props) {
  const { data, isLoading, error } = useReviewRequestsQuery(af);
  const deleteSend = useDeleteReviewSendMutation(af);

  const sendList = useSendReviewRequestsMutation(af);
  const [file, setFile] = useState<File | null>(null);
  const [sendLabel, setSendLabel] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<ReviewSendResult | null>(null);


  if (isLoading) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        Loading review requests…
      </div>
    );
  }
  if (error) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <strong>Review requests unavailable</strong>
        <p style={{ margin: '6px 0 0' }}>{(error as Error).message}</p>
      </div>
    );
  }

  const link = data?.reviewLink ?? '';
  const name = data?.name ?? 'Amada Senior Care';
  const campaigns = data?.campaigns ?? [];

  const emailSubject = `Would you share your experience with ${name}?`;
  // Kept free of any mention of care or service: these land in the same inbox
  // as the emails the dashboard sends, where the Coastal ruling bars that
  // detail, and the disclosure risk is identical whoever presses send.
  const emailBody =
    `Hi there,\n\n` +
    `Thank you for choosing ${name}. If you have a minute, would you leave us a Google ` +
    `review? It helps other families nearby find us.\n\n` +
    `${link}\n\n` +
    `It only takes a moment, and it means a great deal to our team.\n\n` +
    `With thanks,\n${name}`;
  const smsBody =
    `Hi! Thanks for choosing ${name}. If you have a minute, ` +
    `a quick Google review would help other families find us: ${link}`;

  const qrSrc = link
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=png&data=${encodeURIComponent(link)}`
    : '';
  const qrFilename = `${name.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || 'reviews'}-review-qr.png`;

  async function copy(text: string, what: string) {
    const ok = await copyToClipboard(text);
    showToast(ok ? `${what} copied` : `Could not copy ${what.toLowerCase()}`, {
      variant: ok ? 'success' : 'error',
    });
  }

  async function handleQrDownload() {
    try {
      await downloadImage(qrSrc, qrFilename);
      showToast('QR code downloaded', { variant: 'success' });
    } catch (err) {
      showToast(`Download failed: ${(err as Error).message}`, { variant: 'error' });
    }
  }

  return (
    <>
      <div className="ds-section-header">
        <h3>Ask for Reviews</h3>
        <span className="meta">
          {data?.reviewCount !== null && data?.reviewCount !== undefined
            ? `${formatNumber(data.reviewCount)} Google reviews today`
            : 'Review count unavailable'}
        </span>
      </div>

      <div className="ds-panel" style={{ padding: 20, margin: '0 20px 16px' }}>
        {!link ? (
          <p className="rr-empty">
            No Google review link is on file for {af}. Ask the marketing team to add your
            Google Business Profile link and this section will fill in.
          </p>
        ) : (
          <>
            {/* Link + QR */}
            <div className="rr-grid">
              <div className="rr-main">
                <label className="rr-label">Your review link</label>
                <div className="rr-row">
                  <input className="rr-input" readOnly value={link} />
                  <button
                    type="button"
                    className="ds-btn ds-btn-primary"
                    onClick={() => copy(link, 'Link')}
                  >
                    Copy
                  </button>
                </div>
                <p className="rr-hint">
                  This opens the Google review box straight away — no searching, no account
                  hunting.
                </p>
            {/* Ready-to-send copy. Text and buttons share a row: three
                secondary buttons on their own line cost more height than
                they are worth. */}
            <div className="rr-block rr-split">
              <div className="rr-split-text">
                <label className="rr-label">Ready to send</label>
                <p className="rr-note">
                  Copy this into your own email or texting app and send it from your own
                  account.
                </p>
              </div>
              <div className="rr-actions">
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary"
                  onClick={() => copy(emailSubject, 'Subject line')}
                >
                  Copy subject
                </button>
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary"
                  onClick={() => copy(emailBody, 'Email')}
                >
                  Copy email
                </button>
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary"
                  onClick={() => copy(smsBody, 'Text message')}
                >
                  Copy text message
                </button>
              </div>
            </div>

            {/* Upload and send. Always rendered: when the keys are missing the
                controls are disabled and say so, rather than the section
                vanishing and leaving no sign the feature exists. */}
            {(() => {
              const canSend = Boolean(data?.sendingConfigured);
              return (
              <div className="rr-block">
                <label className="rr-label">Upload a spreadsheet</label>
                <p className="rr-note">
                  Upload an xls, xlsx or csv spreadsheet of contacts and their emails to send
                  them review requests. If you need to know what format the spreadsheet should
                  go in, you can{' '}
                  <a href="/api/gbp-review-requests/sample" download>
                    download a sample one here
                  </a>
                  .
                </p>
                <p className="rr-note">
                  <strong>Each email is addressed by first name</strong>, so Jane gets
                  &ldquo;Hello Jane&rdquo;. We read the file to send and then discard it &mdash;
                  no names or addresses are kept and the list can&rsquo;t be viewed afterwards.
                  Every email carries an unsubscribe link, and anyone who opts out is skipped
                  next time.
                </p>
                <form
                  className="rr-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!file) {
                      showToast('Choose a .xlsx or .csv file', { variant: 'error' });
                      return;
                    }
                    if (!confirmed) {
                      showToast('Confirm the list is your own contacts', { variant: 'error' });
                      return;
                    }
                    setResult(null);
                    sendList.mutate(
                      { file, label: sendLabel.trim() || 'Review request' },
                      {
                        onSuccess: (r) => {
                          setResult(r);
                          setFile(null);
                          setSendLabel('');
                          setConfirmed(false);
                          showToast(`Sent ${r.sent} review request${r.sent === 1 ? '' : 's'}`, {
                            variant: 'success',
                          });
                        },
                        onError: (err) => showToast(err.message, { variant: 'error' }),
                      },
                    );
                  }}
                >
                  <div className="rr-form-row">
                    <input
                      className="rr-input"
                      type="file"
                      accept=".xls,.xlsx,.csv"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                      disabled={!canSend}
                      style={{ flex: '2 1 260px' }}
                    />
                    <input
                      className="rr-input"
                      placeholder="Name this send (optional)"
                      value={sendLabel}
                      onChange={(e) => setSendLabel(e.target.value)}
                      maxLength={160}
                      style={{ flex: '1 1 200px' }}
                    />
                  </div>
                  <label className="rr-confirm">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      disabled={!canSend}
                    />
                    <span>
                      These are my own clients or contacts and I&rsquo;m responsible for emailing
                      them.
                    </span>
                  </label>
                  <div className="rr-actions">
                    <button
                      type="submit"
                      className="ds-btn ds-btn-primary"
                      disabled={!canSend || sendList.isPending || !file || !confirmed}
                    >
                      {sendList.isPending ? 'Sending\u2026' : 'Send review requests'}
                    </button>
                  </div>
                </form>
                {result && (
                  <div className="rr-result">
                    <strong>{result.sent} sent.</strong>{' '}
                    {result.suppressed > 0 && `${result.suppressed} skipped (unsubscribed). `}
                    {result.failed > 0 && `${result.failed} failed. `}
                    {result.duplicates > 0 &&
                      `${result.duplicates} duplicate${result.duplicates === 1 ? '' : 's'} ignored. `}
                    {result.rejected > 0 &&
                      `${result.rejected} address${result.rejected === 1 ? '' : 'es'} could not be read. `}
                  </div>
                )}
                {!canSend && (
                  <p className="rr-disabled-note">
                    Sending isn&rsquo;t switched on for this site yet &mdash; this is how it
                    will work. You can download the sample spreadsheet now and have your list
                    ready.
                  </p>
                )}
              </div>
              );
            })()}

              </div>
              <button
                type="button"
                className="rr-qr"
                title="Download the review QR code"
                onClick={handleQrDownload}
              >
                <img src={qrSrc} alt="Review QR code" />
                <span>Download QR</span>
              </button>
            </div>
          </>
        )}

        {/* History */}
        {campaigns.length > 0 && (
          <div className="rr-block">
            <label className="rr-label">Sends so far</label>
            <table className="ds-table" style={{ marginTop: 8, width: '100%' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>What</th>
                  <th>How</th>
                  <th className="num">Sent to</th>
                  <th className="num">Reviews since</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td>
                      {c.label}
                      {c.notes && <div className="rr-hint">{c.notes}</div>}
                    </td>
                    <td>{CHANNEL_LABEL[c.channel]}</td>
                    <td className="num">
                      {c.recipients === null ? '—' : formatNumber(c.recipients)}
                    </td>
                    <td className="num">
                      {c.reviewsSince === null
                        ? '—'
                        : c.reviewsSince > 0
                          ? `+${formatNumber(c.reviewsSince)}`
                          : '0'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="rr-del"
                        title="Remove this entry"
                        onClick={() => {
                          if (confirm(`Remove "${c.label}" from the log?`)) {
                            deleteSend.mutate(c.id, {
                              onError: (err) =>
                                showToast(err.message, { variant: 'error' }),
                            });
                          }
                        }}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
